import React, { useCallback, useMemo, useState } from 'react';
import { Form, Input, Modal, Button, Switch, Popover } from 'antd';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import { useCampaignDuplicateCampaignMutation } from '@hooks/queries/campaign';
import {
  toggleDuplicateProjectModal,
  toggleReachedEntityLimitModal
} from '@redux/actions/modalAction';
import { DuplicateProjectModalStateProps } from '@redux/types/modalType';
import { CrossIcon, SettingsIcon } from '@icons';
import './DuplicateProjectModal.less';

function copyOptionsDescription(
  copyAssets: boolean,
  copyComments: boolean,
  includeMembers: boolean
): string {
  const parts: string[] = [];
  if (copyAssets) parts.push('media');
  if (copyComments) parts.push('comments');
  if (includeMembers) parts.push('members');

  if (parts.length === 0) return 'Only project';
  if (parts.length === 1) return `Project with ${parts[0]}`;

  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(', ');
  return `Project with ${rest} and ${last}`;
}

type FormValues = {
  name: string;
  copyAssets: boolean;
  copyComments: boolean;
  includeMembers: boolean;
};

export default function DuplicateProjectModal({
  visible,
  workspaceId,
  campaignId,
  isPrivate,
  name
}: DuplicateProjectModalStateProps) {
  const dispatch = useDispatch();
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const { user } = useAuth(true, 'internal');
  const [form] = Form.useForm<FormValues>();
  const copyAssets = Form.useWatch('copyAssets', form);
  const copyComments = Form.useWatch('copyComments', form);
  const includeMembers = Form.useWatch('includeMembers', form);
  const nameValue = Form.useWatch('name', form);
  const nameValueLength = nameValue?.length ?? 0;

  const copyOptionsText = useMemo(
    () =>
      copyOptionsDescription(!!copyAssets, !!copyComments, !!includeMembers),
    [copyAssets, copyComments, includeMembers]
  );

  const { mutateAsync: duplicateCampaign, isPending: isDuplicatingCampaign } =
    useCampaignDuplicateCampaignMutation();

  const onDuplicateCampaign = (data: FormValues) => {
    duplicateCampaign(
      {
        workspaceId,
        campaignId,
        name: data.name,
        copyAssets: data.copyAssets,
        copyComments: data.copyComments,
        includeMembers: data.includeMembers
      },
      {
        onSuccess: (res) => {
          if (res.jobId) {
            localStorage.setItem(
              `copy-link-${res.jobId}`,
              `/campaigns/single/${res.newCampaignId}`
            );
          }
          MixpanelService.track(user.id, MixpanelEventType.MANAGE_CAMPAIGNS, {
            campaignName: data.name,
            deleteCampaign: false,
            newCampaign: true,
            privateCampaign: !!isPrivate,
            sharedCampaign: !isPrivate,
            addTaskGroup: false,
            addContributor: false
          });
          dispatch(toggleDuplicateProjectModal(null));
        },
        onError: () => {
          dispatch(
            toggleReachedEntityLimitModal({
              visible: true,
              entity: 'campaign'
            })
          );
        }
      }
    );
  };

  const onCloseModal = useCallback(() => {
    dispatch(toggleDuplicateProjectModal(null));
  }, [dispatch]);

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={492}
      destroyOnClose
      zIndex={1010}
      closeIcon={<CrossIcon />}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={onCloseModal}
      className={classNames('duplicate-project-modal')}
    >
      <Form
        form={form}
        onFinish={onDuplicateCampaign}
        validateTrigger="onChange"
        layout="vertical"
        initialValues={{
          name: name || '',
          copyAssets: true,
          copyComments: true,
          includeMembers: true
        }}
      >
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 24 }}
        >
          Duplicate Project
        </h2>
        <Form.Item
          className="enitity-creation-modal__name-item enitity-creation-modal__name-item--counter"
          name="name"
          label="Project name"
          rules={[
            {
              whitespace: true,
              message: "'name' cannot be empty."
            },
            {
              max: 60,
              message: `${nameValueLength}/60 characters. Name has exceeded character limit.`
            },
            {
              pattern: /^[a-zA-Z 0-9 (".,:'\-—/!?#+=*@&©)]*$/,
              message: `Please use only letters, numbers or the following special symbols .,:()'-—/!?#+=*@&`
            },
            {
              required: true,
              message: 'Name is required.'
            }
          ]}
        >
          <Input
            placeholder="Project name"
            size="large"
            showCount={{
              formatter: ({ count }) => (
                <span className="main-body-text main-body-text--semibold main-body-text--small main-body-text--dark-gray">
                  {count}/60 characters
                </span>
              )
            }}
            maxLength={60}
          />
        </Form.Item>
        <Form.Item name="copyAssets" valuePropName="checked" hidden />
        <Form.Item name="copyComments" valuePropName="checked" hidden />
        <Form.Item name="includeMembers" valuePropName="checked" hidden />
        <div
          className="duplicate-project-modal__options"
          onMouseEnter={() => setIsOptionsOpen(true)}
          onMouseLeave={() => setIsOptionsOpen(false)}
        >
          <div className="duplicate-project-modal__options-description">
            <div className="duplicate-project-modal__options-title">
              Copy options
            </div>
            <div className="duplicate-project-modal__options-text">
              {copyOptionsText}
            </div>
          </div>
          <Popover
            placement="topRight"
            getPopupContainer={(e) => e.parentElement as HTMLElement}
            content={
              <div>
                <div className="duplicate-project-modal__options-title">
                  Copy options
                </div>
                <div
                  className="duplicate-project-modal__switch"
                  onClick={() => form.setFieldValue('copyAssets', !copyAssets)}
                >
                  <Switch checked={!!copyAssets} />
                  <span>Duplicate media</span>
                </div>
                <div
                  className="duplicate-project-modal__switch"
                  onClick={() =>
                    form.setFieldValue('copyComments', !copyComments)
                  }
                >
                  <Switch checked={!!copyComments} />
                  <span>Duplicate comments</span>
                </div>
                <div
                  className="duplicate-project-modal__switch"
                  onClick={() =>
                    form.setFieldValue('includeMembers', !includeMembers)
                  }
                >
                  <Switch checked={!!includeMembers} />
                  <span>Include members</span>
                </div>
              </div>
            }
            trigger="hover"
            showArrow={false}
            open={isOptionsOpen}
          >
            <SettingsIcon />
          </Popover>
        </div>

        <div className="modal_container__actions">
          <Button
            type="text"
            onClick={onCloseModal}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            size="large"
            loading={isDuplicatingCampaign}
            htmlType="submit"
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            Duplicate
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
